/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { i as isAxiosInstance } from './vueprint.fd68f7a1.mjs';
import { B as BusService } from './vueprint.1f7141fe.mjs';
import { L as LocalStorageService } from './vueprint.19cdf2cc.mjs';
import { M as MiliCron } from './vueprint.5d216d52.mjs';
import { g as getDebugger } from './vueprint.b84f56b4.mjs';
import { ref, computed, watch } from 'vue';
import { DateTime } from 'luxon';

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _bus, _api, _ls, _cron, _booted, _storedBearer, _storedExpiration, _storedUserIdentity, _calculatedAuthenticationExpiresIn, _watcher, _refreshable, _refreshableWatcher, _authenticated, _identified, _user, _tokenRefreshAbortController, _tokenRefresh, _tokenRefreshBuffer, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn, _updateFromLocalStorageService, updateFromLocalStorageService_fn, _doCalculations, doCalculations_fn, _tryToRefreshToken, tryToRefreshToken_fn;
const debug = getDebugger("Identity");
class IdentityService {
  /**
   * Create a new Identity instance
   * @param bus A BusService instance
   * @param ls A LocalStorageService instance
   * @param cron A MiliCron instance
   * @param api An Axios instance
   * @param tokenRefresh A token refresh callback
   * @param tokenRefreshBuffer The amount of time in milliseconds before the token expires before a token is considered refreshable (default: 5 minutes)
   */
  constructor(bus, ls, cron, api, tokenRefresh, tokenRefreshBuffer = 60 * 5) {
    __privateAdd(this, _doUpdateFromLocalStorageService);
    __privateAdd(this, _updateFromLocalStorageService);
    __privateAdd(this, _doCalculations);
    __privateAdd(this, _tryToRefreshToken);
    __privateAdd(this, _bus, void 0);
    __privateAdd(this, _api, void 0);
    __privateAdd(this, _ls, void 0);
    __privateAdd(this, _cron, void 0);
    __privateAdd(this, _booted, void 0);
    __privateAdd(this, _storedBearer, void 0);
    __privateAdd(this, _storedExpiration, void 0);
    __privateAdd(this, _storedUserIdentity, void 0);
    __privateAdd(this, _calculatedAuthenticationExpiresIn, ref(void 0));
    __privateAdd(this, _watcher, void 0);
    __privateAdd(this, _refreshable, void 0);
    __privateAdd(this, _refreshableWatcher, void 0);
    __privateAdd(this, _authenticated, void 0);
    __privateAdd(this, _identified, void 0);
    __privateAdd(this, _user, void 0);
    __privateAdd(this, _tokenRefreshAbortController, void 0);
    __privateAdd(this, _tokenRefresh, void 0);
    __privateAdd(this, _tokenRefreshBuffer, void 0);
    if (!(bus instanceof BusService)) {
      debug({ bus });
      throw new Error("Invalid or missing BusService instance");
    }
    if (!(ls instanceof LocalStorageService)) {
      debug({ ls });
      throw new Error("Invalid or missing LocalStorageService instance");
    }
    if (!(cron instanceof MiliCron)) {
      debug({ cron });
      throw new Error("Invalid or missing MiliCron instance");
    }
    if (!isAxiosInstance(api)) {
      debug({ api });
      throw new Error("Invalid or missing Axios instance");
    }
    __privateSet(this, _booted, ref(false));
    __privateSet(this, _bus, bus);
    __privateSet(this, _api, api);
    __privateSet(this, _ls, ls);
    __privateSet(this, _tokenRefresh, tokenRefresh);
    __privateSet(this, _tokenRefreshBuffer, tokenRefreshBuffer);
    __privateSet(this, _cron, cron);
    __privateSet(this, _storedBearer, ref(void 0));
    __privateSet(this, _storedExpiration, ref(void 0));
    __privateSet(this, _storedUserIdentity, ref(void 0));
    __privateSet(this, _calculatedAuthenticationExpiresIn, ref(void 0));
    __privateSet(this, _refreshable, computed(() => {
      return __privateGet(this, _calculatedAuthenticationExpiresIn).value !== void 0 && __privateGet(this, _calculatedAuthenticationExpiresIn).value > 0 && __privateGet(this, _calculatedAuthenticationExpiresIn).value <= __privateGet(this, _tokenRefreshBuffer);
    }));
    __privateSet(this, _authenticated, computed(() => {
      return __privateGet(this, _booted).value === true && __privateGet(this, _storedBearer).value !== void 0 && __privateGet(this, _calculatedAuthenticationExpiresIn).value !== void 0 && __privateGet(this, _calculatedAuthenticationExpiresIn).value > 0;
    }));
    __privateSet(this, _identified, computed(() => {
      return __privateGet(this, _authenticated).value && __privateGet(this, _storedUserIdentity).value !== void 0 && __privateGet(this, _storedUserIdentity).value !== null && Object.keys(__privateGet(this, _storedUserIdentity).value).length > 0;
    }));
    __privateSet(this, _user, computed(() => {
      if (!__privateGet(this, _identified).value) {
        return void 0;
      }
      return {
        ...__privateGet(this, _storedUserIdentity).value
      };
    }));
    __privateSet(this, _tokenRefreshAbortController, ref(void 0));
  }
  /**
   * Whether or not the Identity service has booted
   */
  get booted() {
    return __privateGet(this, _booted);
  }
  /**
   * Whether or not the visitor is authenticated
   */
  get authenticated() {
    return __privateGet(this, _authenticated);
  }
  /**
   * Whether or not the visitor is identified
   */
  get identified() {
    return __privateGet(this, _identified);
  }
  /**
   * The user's identity
   */
  get user() {
    return __privateGet(this, _user);
  }
  /**
   * Whether or not the token is refreshable
   */
  get refreshable() {
    return __privateGet(this, _refreshable);
  }
  /**
   * The time until the authentication expires
   */
  get ttl() {
    return __privateGet(this, _calculatedAuthenticationExpiresIn);
  }
  /**
   * Save the bearer token, expiration, and user identity to the LocalStorageService and update the authentication & identification state
   * @param bearer The bearer token
   * @param expiration The expiration time of the token
   * @param identity The user's identity
   * @returns void
   */
  login(bearer, expiration, identity) {
    if (!__privateGet(this, _booted)) {
      debug("Login not processed because not booted");
      return;
    }
    __privateGet(this, _ls).set("bearer", bearer);
    __privateGet(this, _storedBearer).value = bearer;
    __privateGet(this, _ls).set("expiration", expiration);
    __privateGet(this, _storedExpiration).value = expiration;
    __privateGet(this, _ls).set("user.identity", identity);
    __privateGet(this, _storedUserIdentity).value = identity;
    __privateMethod(this, _doCalculations, doCalculations_fn).call(this);
    __privateGet(this, _bus).emit(
      "identity:login",
      {
        local: true,
        crossTab: true
      },
      bearer,
      expiration,
      identity
    );
  }
  /**
   * Remove the bearer token, expiration, and user identity from the LocalStorageService and update the authentication & identification state
   * @returns void
   */
  logout() {
    __privateGet(this, _ls).remove("bearer");
    __privateGet(this, _storedBearer).value = void 0;
    __privateGet(this, _ls).remove("expiration");
    __privateGet(this, _storedExpiration).value = void 0;
    __privateGet(this, _ls).remove("user.identity");
    __privateGet(this, _storedUserIdentity).value = void 0;
    __privateGet(this, _bus).emit("identity:logout", {
      local: true,
      crossTab: true
    });
  }
  /**
   * Boot the Identity service
   */
  boot() {
    __privateGet(this, _bus).on("api:unauthorized", this.logout.bind(this), { local: true, crossTab: true });
    __privateGet(this, _bus).on("identity:login", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true,
      crossTab: true,
      immediate: true
    });
    __privateGet(this, _bus).on("identity:logout", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true,
      crossTab: true,
      immediate: true
    });
    __privateGet(this, _bus).on("tab:active", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true
    });
    __privateGet(this, _bus).on("authentication:refreshable", __privateMethod(this, _tryToRefreshToken, tryToRefreshToken_fn).bind(this), {
      local: true,
      crossTab: true,
      immediate: true
    });
    __privateGet(this, _cron).$on("*/250 * * * * *", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this));
    __privateGet(this, _cron).$on("* * * * *", __privateMethod(this, _doCalculations, doCalculations_fn).bind(this));
    __privateSet(this, _watcher, watch(
      () => __privateGet(this, _calculatedAuthenticationExpiresIn).value,
      (updated, previous) => {
        if (updated === previous) {
          return;
        }
        if (updated === void 0 && previous !== void 0 && __privateGet(this, _storedBearer).value !== void 0) {
          debug("Authentication Expired. Logging Out.");
          this.logout();
        } else if (updated !== void 0 && updated <= 0) {
          debug("Authentication Expired. Logging Out.");
          this.logout();
        }
      },
      {
        immediate: true
      }
    ));
    __privateSet(this, _refreshableWatcher, watch(
      () => __privateGet(this, _refreshable).value,
      (refreshable) => {
        if (refreshable) {
          debug("Token is refreshable");
          __privateGet(this, _bus).emit("authentication:refreshable", {
            local: true,
            crossTab: true
          });
        }
      }
    ));
    __privateMethod(this, _updateFromLocalStorageService, updateFromLocalStorageService_fn).call(this, true);
  }
  /**
   * Shutdown the Identity service
   */
  shutdown() {
    __privateGet(this, _bus).off("api:unauthorized", this.logout.bind(this), { local: true, crossTab: true });
    __privateGet(this, _bus).off("identity:login", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true,
      crossTab: true
    });
    __privateGet(this, _bus).off("tab:active", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true
    });
    __privateGet(this, _bus).off("identity:logout", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this), {
      local: true,
      crossTab: true
    });
    __privateGet(this, _cron).$off("*/250 * * * * *", __privateMethod(this, _doUpdateFromLocalStorageService, doUpdateFromLocalStorageService_fn).bind(this));
    __privateGet(this, _cron).$off("* * * * *", __privateMethod(this, _doCalculations, doCalculations_fn).bind(this));
    if (__privateGet(this, _watcher)) {
      __privateGet(this, _watcher).call(this);
    }
    if (__privateGet(this, _refreshableWatcher)) {
      __privateGet(this, _refreshableWatcher).call(this);
    }
    __privateGet(this, _booted).value = false;
    debug("Shutdown");
  }
  /**
   * Force a refresh of the token
   * @private
   * @remarks This method is not intended to be used by the consumer of the Identity service, but is for development and testing purposes
   */
  async forceRefreshToken() {
    if (__privateGet(this, _booted).value) {
      await __privateMethod(this, _tryToRefreshToken, tryToRefreshToken_fn).call(this);
    }
  }
  /**
   * Make the token refreshable
   * @private
   * @remarks This method is not intended to be used by the consumer of the Identity service, but is for development and testing purposes
   */
  $makeRefreshable() {
    if (__privateGet(this, _booted).value && "string" === typeof __privateGet(this, _storedBearer).value && "string" === typeof __privateGet(this, _storedExpiration).value) {
      const newExpiration = DateTime.now().plus({ seconds: __privateGet(this, _tokenRefreshBuffer) }).toISO();
      __privateGet(this, _ls).set("expiration", newExpiration);
      __privateGet(this, _storedExpiration).value = newExpiration;
      __privateMethod(this, _doCalculations, doCalculations_fn).call(this);
    }
  }
}
_bus = new WeakMap();
_api = new WeakMap();
_ls = new WeakMap();
_cron = new WeakMap();
_booted = new WeakMap();
_storedBearer = new WeakMap();
_storedExpiration = new WeakMap();
_storedUserIdentity = new WeakMap();
_calculatedAuthenticationExpiresIn = new WeakMap();
_watcher = new WeakMap();
_refreshable = new WeakMap();
_refreshableWatcher = new WeakMap();
_authenticated = new WeakMap();
_identified = new WeakMap();
_user = new WeakMap();
_tokenRefreshAbortController = new WeakMap();
_tokenRefresh = new WeakMap();
_tokenRefreshBuffer = new WeakMap();
_doUpdateFromLocalStorageService = new WeakSet();
doUpdateFromLocalStorageService_fn = function() {
  __privateMethod(this, _updateFromLocalStorageService, updateFromLocalStorageService_fn).call(this);
};
_updateFromLocalStorageService = new WeakSet();
updateFromLocalStorageService_fn = function(first = false) {
  if (!__privateGet(this, _ls).loaded) {
    __privateGet(this, _ls).promise.then(() => {
      __privateMethod(this, _updateFromLocalStorageService, updateFromLocalStorageService_fn).call(this, first);
    });
    return;
  }
  __privateGet(this, _storedBearer).value = __privateGet(this, _ls).get("bearer");
  __privateGet(this, _storedExpiration).value = __privateGet(this, _ls).get("expiration");
  __privateGet(this, _storedUserIdentity).value = __privateGet(this, _ls).get("user.identity");
  if (first) {
    __privateMethod(this, _doCalculations, doCalculations_fn).call(this);
    __privateGet(this, _booted).value = true;
    debug("Booted");
  }
};
_doCalculations = new WeakSet();
doCalculations_fn = function() {
  if (!__privateGet(this, _storedExpiration).value) {
    __privateGet(this, _calculatedAuthenticationExpiresIn).value = void 0;
    return;
  } else {
    const expiration = DateTime.fromISO(__privateGet(this, _storedExpiration).value);
    if (!expiration.isValid) {
      __privateGet(this, _calculatedAuthenticationExpiresIn).value = void 0;
      return;
    }
    const now = DateTime.now();
    const diff = expiration.diff(now, "seconds");
    __privateGet(this, _calculatedAuthenticationExpiresIn).value = diff.seconds;
  }
};
_tryToRefreshToken = new WeakSet();
tryToRefreshToken_fn = async function() {
  if (__privateGet(this, _bus) && __privateGet(this, _api)) {
    const main = await __privateGet(this, _bus).isMain(500);
    if (main) {
      if (__privateGet(this, _tokenRefreshAbortController).value) {
        __privateGet(this, _tokenRefreshAbortController).value.abort();
      }
      __privateGet(this, _tokenRefreshAbortController).value = new AbortController();
      try {
        const { bearer, expiration } = await __privateGet(this, _tokenRefresh).call(this, __privateGet(this, _api), __privateGet(this, _tokenRefreshAbortController).value.signal);
        __privateGet(this, _ls).set("bearer", bearer);
        __privateGet(this, _storedBearer).value = bearer;
        __privateGet(this, _ls).set("expiration", expiration);
        __privateGet(this, _storedExpiration).value = expiration;
        __privateMethod(this, _doCalculations, doCalculations_fn).call(this);
      } catch (e) {
        debug("Failed to refresh token");
      }
    } else {
      debug("Not main tab. Allowing main tab to refresh token.");
    }
  }
};

const identity = {
  __proto__: null,
  IdentityService: IdentityService
};

export { IdentityService as I, identity as i };
//# sourceMappingURL=vueprint.c6b8bf52.mjs.map
